import { useQuery } from '@apollo/client'
import { Typography } from '@mui/material'
import { TemplateType } from '__generated__/graphql'
import { StyledContainer } from 'candidate/components/styles'
import {
    GetCandidateRecruitingPreferences,
    GetRecruitmentPreferenceNoteTemplate,
} from 'candidate/graphql/recruitingPreferences.gql'
import { useMemo, useState } from 'react'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { ReactComponent as EmptyPreferences } from 'shared/assets/new_empty_preferences.svg'
import UnsavedChangesModal from 'shared/components/UnsavedChangesModal/UnsavedChangesModal'
import UnsavedChangesModalNavBlocker from 'shared/components/UnsavedChangesModal/UnsavedChangesModalNavBlocker'
import {
    Separator,
    StyledCard,
    StyledEmptyCardContent,
    TitleContainer,
} from 'shared/style/styled'
import { getPxAsRem } from 'shared/utils'
import { getUserIdFromToken } from 'shared/utils/getUserIdFromToken'
import Company from './Company'
import Industry from './Industry'
import Location from './Location'
import Notes from './Notes'
import PreferDate from './PreferDate'
import Sector from './Sector'

const Preferences = () => {
    usePageTitle('Preferences')
    const id = getUserIdFromToken() ?? ''
    const { data, refetch } = useQuery(GetCandidateRecruitingPreferences, {
        variables: { id },
    })

    const { data: noteTemplateData } = useQuery(
        GetRecruitmentPreferenceNoteTemplate,
        {
            variables: {
                templateType: TemplateType.RecruitmentPreference,
            },
        }
    )

    const [unsavedChangesExist, setUnsavedChangesExist] =
        useState<boolean>(false)
    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
        useState<boolean>(false)
    const [nextEditSection, setNextEditSection] = useState<string | null>(null)

    const [editArraySection, setEditArraySection] = useState([
        {
            sectionName: 'location',
            isEdit: false,
        },
        {
            sectionName: 'industry',
            isEdit: false,
        },
        {
            sectionName: 'sector',
            isEdit: false,
        },
        {
            sectionName: 'company',
            isEdit: false,
        },
        {
            sectionName: 'preferDate',
            isEdit: false,
        },
        {
            sectionName: 'notes',
            isEdit: false,
        },
    ])

    const handleEditArray = (sectionName: string, cancelEdit?: boolean) => {
        if (unsavedChangesExist && !cancelEdit) {
            setNextEditSection(sectionName)
            setUnsavedChangesModalOpen(true)
        } else {
            let tmp = [...editArraySection]
            tmp = tmp.map((item) => {
                item.isEdit =
                    item.sectionName === sectionName && cancelEdit
                        ? false
                        : item.sectionName === sectionName
                          ? !item.isEdit
                          : false
                return item
            })
            setEditArraySection([...tmp])
        }
    }

    const startDate = useMemo(
        () => data?.candidate?.startDate?.map((s) => s?.value || '') ?? [],
        [data]
    )

    return (
        <StyledContainer>
            <UnsavedChangesModalNavBlocker
                unsavedChangesExist={unsavedChangesExist}
            />
            <UnsavedChangesModal
                open={unsavedChangesModalOpen}
                onStayClick={() => {
                    setNextEditSection(null)
                    setUnsavedChangesModalOpen(false)
                }}
                onLeaveClick={() => {
                    let tmp = [...editArraySection]
                    tmp = tmp.map((item) => {
                        item.isEdit =
                            item.sectionName === nextEditSection
                                ? !item.isEdit
                                : false
                        return item
                    })
                    setEditArraySection([...tmp])
                    setNextEditSection(null)
                    setUnsavedChangesModalOpen(false)
                }}
            />
            <TitleContainer>
                <Typography variant="h2">Recruiting Preferences</Typography>
                <Typography variant="subtitle2" sx={{ mt: getPxAsRem(8) }}>
                    These preferences affect the jobs we recommend to you.
                </Typography>
            </TitleContainer>
            <StyledCard>
                {
                    // Candidate exists.
                    !!data?.candidate &&
                    // Candidate has more than one intake.
                    Array.isArray(data.candidate.candidateIntakes) &&
                    data.candidate.candidateIntakes.length > 1 &&
                    // Candidate's intakes all have completion dates.
                    data.candidate.candidateIntakes.every(
                        (intake) => intake.completionDate !== null
                    ) ? (
                        <>
                            <Location
                                candidate={data.candidate}
                                handleEditArray={handleEditArray}
                                editArray={editArraySection}
                                sectionName="location"
                                setUnsavedChangesExist={setUnsavedChangesExist}
                                refetch={refetch}
                            />

                            <Separator />

                            <Industry
                                candidate={data.candidate}
                                handleEditArray={handleEditArray}
                                editArray={editArraySection}
                                sectionName="industry"
                                setUnsavedChangesExist={setUnsavedChangesExist}
                                refetch={refetch}
                            />

                            <Separator />

                            <Sector
                                candidate={data.candidate}
                                handleEditArray={handleEditArray}
                                editArray={editArraySection}
                                sectionName="sector"
                                setUnsavedChangesExist={setUnsavedChangesExist}
                                refetch={refetch}
                            />

                            <Separator />

                            <Company
                                candidate={data.candidate}
                                handleEditArray={handleEditArray}
                                editArray={editArraySection}
                                sectionName="company"
                                setUnsavedChangesExist={setUnsavedChangesExist}
                                refetch={refetch}
                            />

                            <Separator />

                            <PreferDate
                                preferStartDate={startDate}
                                handleEditArray={handleEditArray}
                                editArray={editArraySection}
                                sectionName="preferDate"
                                candidateId={data.candidate.id}
                                setUnsavedChangesExist={setUnsavedChangesExist}
                                refetch={refetch}
                            />

                            <Separator />

                            <Notes
                                candidateId={data.candidate.id}
                                notesData={data.candidate.preferencesNote ?? ''}
                                handleEditArray={handleEditArray}
                                editArray={editArraySection}
                                refetch={refetch}
                                sectionName="notes"
                                setUnsavedChangesExist={setUnsavedChangesExist}
                                templateId={
                                    noteTemplateData?.noteTemplate?.id ?? ''
                                }
                            />
                        </>
                    ) : (
                        <StyledEmptyCardContent>
                            <EmptyPreferences />
                            <Typography variant="body1">
                                Please complete all questionnaire tasks to view
                                your recruiting preferences.
                            </Typography>
                        </StyledEmptyCardContent>
                    )
                }
            </StyledCard>
        </StyledContainer>
    )
}

export default Preferences
